import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import Callout from '@wetransfer/transfer/component/Callout/Callout';
import Button from '@wetransfer/transfer/component/form/element/button';
import ProWallpaperPreview from '@wetransfer/transfer/component/ProWallpaperPreview/ProWallpaperPreview';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import { useCurrentTeam } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import I18n from '@wetransfer/transfer/modules/i18n';
import Router from '@wetransfer/transfer/modules/router';
import { State } from '@wetransfer/transfer/store/types';

import './ProOnboardingCallout.scss';

// We show the callout on the user's 2nd visit
const SHOW_ON_VISIT_COUNT = 2;

interface Props {
  calloutStyles: React.CSSProperties;
}

export const ProOnboardingCallout: React.FC<Props> = ({ calloutStyles }) => {
  const [isCalloutOpen, setIsCalloutOpen] = useState(true);
  const [isSmallViewport, setIsSmallViewport] = useState(false);
  const { currentTeam, isCurrentTeamOfMany } = useCurrentTeam();

  const { channel, auth, session, isPanelOpen } = useSelector(
    (state: State) => ({
      channel: state.channel,
      auth: state.auth,
      session: state.session,
      isPanelOpen: state.route.isPanelOpen,
    })
  );

  const calloutRef = useRef(null);

  const numberOfTimesSeen = Number(
    Cookie.get(AppConfig.cookies.teamsOnboardingCallout.name)
  );

  const showCalloutOnNumberOfVisits = numberOfTimesSeen === SHOW_ON_VISIT_COUNT;

  const isUserOnTeam = Boolean(currentTeam?.id) && isCurrentTeamOfMany;
  const isAdminOfTeam = currentTeam?.creatorId === auth.auth0UserId;
  const isBrandingSet = channel.wallpapers.length > 0;

  // Case 1: The user is a team invitee and branding has already been set
  const showTeamInviteeCallout =
    showCalloutOnNumberOfVisits &&
    isUserOnTeam &&
    !isAdminOfTeam &&
    isCurrentTeamOfMany &&
    isBrandingSet;

  // Case 2: The user has a team and branding has not yet been set
  const showSetBrandingCallout =
    showCalloutOnNumberOfVisits &&
    isUserOnTeam &&
    isCurrentTeamOfMany &&
    !isBrandingSet;

  // Case 3: The user is an individual Pro user and branding has not yet been set
  const showIndividualProUserCallout =
    showCalloutOnNumberOfVisits && !isCurrentTeamOfMany && !isBrandingSet;

  // Increment number of visits
  useEffect(() => {
    if (numberOfTimesSeen < SHOW_ON_VISIT_COUNT && !isPanelOpen) {
      Cookie.set(
        AppConfig.cookies.teamsOnboardingCallout.name,
        numberOfTimesSeen + 1,
        { domain: session.domain }
      );
    }
  }, []);

  function handleCalloutClick() {
    Router.navigateTo('/profile');
    dismissCallout();
  }

  function handleAdminCalloutClick() {
    if (!isBrandingSet) {
      Analytics.trackSnowplowEvent({
        category: 'teams_onboarding',
        action: 'go_to_branding',
        label: 'callout_wallpaper',
      });
    }
    handleCalloutClick();
  }

  function dismissCallout() {
    Cookie.set(AppConfig.cookies.teamsOnboardingCallout.name, 999, {
      domain: session.domain,
    });

    const now = new Date();

    const formattedDate = `${
      now.getMonth() + 1
    }/${now.getDate()}/${now.getFullYear()}`;

    Cookie.set(
      AppConfig.cookies.teamsOnboardingCalloutDate.name,
      formattedDate,
      {
        domain: session.domain,
      }
    );
    setIsCalloutOpen(false);
  }

  function dismissCalloutPositive() {
    Analytics.trackSnowplowEvent({
      category: 'teams_onboarding',
      action: 'got_it',
      label: 'callout_wallpaper',
    });
    dismissCallout();
  }

  function dismissCalloutNegative() {
    Analytics.trackSnowplowEvent({
      category: 'teams_onboarding',
      action: 'not_now',
      label: 'callout_wallpaper',
    });
    dismissCallout();
  }

  function updateWallpaperPreviewStyles() {
    setIsSmallViewport(true);
  }

  const teamInviteeCallout = (
    <Callout
      ref={calloutRef}
      isOpen={isCalloutOpen}
      calloutStyles={calloutStyles}
      updateWallpaperPreviewStyles={updateWallpaperPreviewStyles}
    >
      <h2 className="ProOnboardingCallout__title">
        {I18n.t('callout.pro_onboarding.invitee.title')}
      </h2>
      <ProWallpaperPreview isSmallViewport={isSmallViewport} />
      <div className="ProOnboardingCallout__text-content">
        <p className="ProOnboardingCallout__text-content-paragraph">
          {I18n.t('callout.pro_onboarding.invitee.text_1')}
        </p>
        <p
          className="ProOnboardingCallout__text-content-paragraph"
          dangerouslySetInnerHTML={{
            __html: I18n.t('callout.pro_onboarding.invitee.text_2'),
          }}
        />
      </div>
      <Button
        text={I18n.t('callout.pro_onboarding.invitee.cta')}
        className="button--secondary ProOnboardingCallout__cta-button"
        onClick={dismissCalloutPositive}
      />
    </Callout>
  );

  const setBrandingCallout = (
    <Callout
      ref={calloutRef}
      isOpen={isCalloutOpen}
      calloutStyles={calloutStyles}
      updateWallpaperPreviewStyles={updateWallpaperPreviewStyles}
    >
      <h2 className="ProOnboardingCallout__title">
        {I18n.t('callout.pro_onboarding.invitee.title')}
      </h2>
      <ProWallpaperPreview isSmallViewport={isSmallViewport} />
      <div className="ProOnboardingCallout__text-content">
        <p className="ProOnboardingCallout__text-content-paragraph">
          {I18n.t('callout.pro_onboarding.invitee.text_1')}
        </p>
        <p className="ProOnboardingCallout__text-content-paragraph">
          {I18n.t('callout.pro_onboarding.admin.text_2')}
        </p>
      </div>
      <Button
        text={I18n.t('callout.pro_onboarding.admin.cta')}
        className="button--secondary ProOnboardingCallout__cta-button"
        onClick={handleAdminCalloutClick}
      />
      <Button
        text={I18n.t('callout.pro_onboarding.not_now')}
        className="ProOnboardingCallout__secondary-cta-button"
        onClick={dismissCalloutNegative}
      />
    </Callout>
  );

  const individualProUserCallout = (
    <Callout
      ref={calloutRef}
      isOpen={isCalloutOpen}
      calloutStyles={calloutStyles}
      updateWallpaperPreviewStyles={updateWallpaperPreviewStyles}
    >
      <h2 className="ProOnboardingCallout__title">
        {I18n.t('callout.pro_onboarding.individual.title')}
      </h2>
      <ProWallpaperPreview isSmallViewport={isSmallViewport} />
      <p className="ProOnboardingCallout__text-content">
        {I18n.t('callout.pro_onboarding.individual.text')}
      </p>
      <Button
        text={I18n.t('callout.pro_onboarding.individual.cta')}
        className="button--secondary ProOnboardingCallout__cta-button"
        onClick={handleCalloutClick}
      />
      <Button
        text={I18n.t('callout.pro_onboarding.not_now')}
        className="ProOnboardingCallout__secondary-cta-button"
        onClick={dismissCallout}
      />
    </Callout>
  );

  return (
    <React.Fragment>
      {showTeamInviteeCallout && teamInviteeCallout}
      {showSetBrandingCallout && setBrandingCallout}
      {showIndividualProUserCallout && individualProUserCallout}
    </React.Fragment>
  );
};

export default ProOnboardingCallout;
